import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EmailBuilderSettingsTextInput } from "../EmailBuilderSettingsTextInput";
import { UrlField } from "../UrlField";
import { ButtonNew as Button } from "components/buttonNew";
import Portal from "elements/Portal";
import { useAppSelector } from "hooks/redux";
import {
  baselineSocialGap,
  baselineSocialIconSize,
  baselineSocialPadding,
} from "../../domain/baselines";
import {
  addCustomSocialBlockLink,
  addPresetSocialBlockLink,
  availableSocialPresetNetworks,
  moveSocialBlockLink,
  MIN_SOCIAL_BLOCK_LINKS,
  patchSocialBlockLinkById,
  removeSocialBlockLink,
  resolveSocialIconUrl,
  resolveSocialLinkIconUrl,
  resolveSocialLinkTitle,
  SOCIAL_LABELS,
  type SocialPresetNetwork,
} from "../../domain/media";
import type {
  SocialBlock,
  SocialBlockLink,
} from "../../domain/types";
import { emailBuilderFacilityProfile } from "../../operations/emailBuilderFacilityProfile";
import { emailBuilderMediaItemToMediaRef } from "../../operations/emailBuilderMediaRef";
import { EMAIL_BUILDER_DOM_ID_PREFIX, useStableDomId } from "../../useStableDomId";
import { EmailBuilderMediaGalleryModal } from "../../shell/panel/blockSettings/EmailBuilderMediaGalleryModal";
import {
  EmailBuilderAlignmentControl,
  EmailBuilderBlockSettingsIconButton,
  EmailBuilderBoundedRange,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import {
  EMAIL_BUILDER_SETTINGS_DEFAULT_OVERLAY_PORTAL_ID,
  useEmailBuilderSettingsOverlayPortalId,
} from "../../shell/canvas/settings/emailBuilderSettingsOverlayPortal";
import { SettingSection } from "../controls/SettingSection";
import { isPaddingInactive } from "../settingsSectionActivation";
import type { BlockSettingsProps } from "./types";

const SOCIAL_ADD_MENU_WIDTH_PX = 220;
const SOCIAL_ADD_MENU_GAP_PX = 6;
const SOCIAL_ADD_MENU_EDGE_GAP_PX = 8;
const SOCIAL_ADD_MENU_PANEL_INSET_PX = 12;

function resolveSocialAddMenuBounds(trigger: HTMLElement): {
  minLeft: number;
  maxLeft: number;
  maxWidth: number;
} {
  const viewportLeft = SOCIAL_ADD_MENU_EDGE_GAP_PX;
  const viewportRight = window.innerWidth - SOCIAL_ADD_MENU_PANEL_INSET_PX;
  let boundsLeft = viewportLeft;
  let boundsRight = viewportRight;

  const panel =
    trigger.closest(".email-builder-settings-side-panel--right") ??
    trigger.closest(".email-builder-settings-bottom-sheet-content") ??
    trigger.closest(".email-builder-settings-bottom-sheet-body");

  if (panel instanceof HTMLElement) {
    const panelRect = panel.getBoundingClientRect();
    boundsLeft = Math.max(boundsLeft, panelRect.left + SOCIAL_ADD_MENU_PANEL_INSET_PX);
    boundsRight = Math.min(boundsRight, panelRect.right - SOCIAL_ADD_MENU_PANEL_INSET_PX);
  }

  const availableWidth = Math.max(0, boundsRight - boundsLeft);
  const maxWidth = Math.min(SOCIAL_ADD_MENU_WIDTH_PX, availableWidth);
  const minLeft = boundsLeft;
  const maxLeft = Math.max(minLeft, boundsRight - maxWidth);

  return { minLeft, maxLeft, maxWidth };
}

function resolveSocialLinkIconMediaId(link: SocialBlockLink): number | null {
  if (link.icon?.kind === "library") {
    const parsed = Number.parseInt(link.icon.mediaId, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

type SocialIconPickerTarget =
  | { kind: "replace"; linkId: string }
  | { kind: "add-custom" };

function SocialLinkCard({
  link,
  iconPreviewUrl,
  canRemove,
  canMoveUp,
  canMoveDown,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
  onChangeIcon,
}: {
  link: SocialBlockLink;
  iconPreviewUrl: string;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onPatch: (patch: Partial<Pick<SocialBlockLink, "url" | "title" | "alt">>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onChangeIcon: () => void;
}) {
  const { t } = useTranslation();
  const [moreOptions, setMoreOptions] = useState(false);
  const titleFieldId = useStableDomId(EMAIL_BUILDER_DOM_ID_PREFIX);
  const altFieldId = useStableDomId(EMAIL_BUILDER_DOM_ID_PREFIX);
  const isCustomLink = link.network === "custom";
  const rowTitle = resolveSocialLinkTitle(link);

  return (
    <div className={"email-builder-settings-social-link-card"}>
      <div className={"email-builder-settings-social-link-card-top"}>
        <div className={"email-builder-settings-social-link-icon-preview"}>
          {iconPreviewUrl.length > 0 ? (
            <img src={iconPreviewUrl} alt="" width={20} height={20} />
          ) : (
            <span
              className={"email-builder-settings-social-link-icon-placeholder"}
              aria-hidden="true"
            />
          )}
        </div>

        <div className={"email-builder-settings-social-link-card-meta"}>
          <div className={"email-builder-settings-social-link-card-title"}>
            {link.title?.trim() ? rowTitle : t(rowTitle)}
          </div>
        </div>

        <div className={"email-builder-settings-social-link-card-toolbar"}>
          <EmailBuilderBlockSettingsIconButton
            label={t("Move up")}
            icon={faArrowUp}
            disabled={!canMoveUp}
            onClick={onMoveUp}
          />
          <EmailBuilderBlockSettingsIconButton
            label={t("Move down")}
            icon={faArrowDown}
            disabled={!canMoveDown}
            onClick={onMoveDown}
          />
          <EmailBuilderBlockSettingsIconButton
            label={t("Delete")}
            icon={faXmark}
            variant="danger"
            disabled={!canRemove}
            onClick={onRemove}
          />
        </div>
      </div>

      <UrlField
        label={t("Url")}
        mode="social"
        value={link.url}
        onChange={url => onPatch({ url })}
      />

      <Button
        type="link"
        size="small"
        className={"email-builder-settings-social-link-disclosure"}
        aria-expanded={moreOptions}
        icon={
          <FontAwesomeIcon icon={moreOptions ? faChevronUp : faChevronDown} aria-hidden="true" />
        }
        onClick={() => setMoreOptions(current => !current)}
      >
        {t("More options")}
      </Button>

      {moreOptions ? (
        <div className={"email-builder-settings-social-link-card-advanced"}>
          {isCustomLink ? (
            <div className={"email-builder-settings-social-link-image-actions"}>
              <Button type="primary" size="small" onClick={onChangeIcon}>
                {t("Change image")}
              </Button>
            </div>
          ) : null}
          <EmailBuilderSettingsTextInput
            id={titleFieldId}
            prefix={t("Title")}
            labelHidden
            value={link.title ?? ""}
            onChange={event => onPatch({ title: event.target.value })}
          />
          <EmailBuilderSettingsTextInput
            id={altFieldId}
            prefix={t("Alt text")}
            labelHidden
            value={link.alt ?? ""}
            onChange={event => onPatch({ alt: event.target.value })}
          />
        </div>
      ) : null}
    </div>
  );
}

function SocialAddLinkPopover({
  availablePresets,
  onAddPreset,
  onRequestAddCustom,
}: {
  availablePresets: SocialPresetNetwork[];
  onAddPreset: (network: SocialPresetNetwork) => void;
  onRequestAddCustom: () => void;
}) {
  const { t } = useTranslation();
  const portalId = useEmailBuilderSettingsOverlayPortalId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const canAdd = availablePresets.length > 0;
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    maxWidth: number;
    positionMode: "fixed" | "absolute";
    placement: "above" | "below";
  } | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const { minLeft, maxLeft, maxWidth } = resolveSocialAddMenuBounds(trigger);
    let left = rect.left;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const spaceBelow = Math.max(
      0,
      window.innerHeight - SOCIAL_ADD_MENU_EDGE_GAP_PX - (rect.bottom + SOCIAL_ADD_MENU_GAP_PX),
    );
    const spaceAbove = Math.max(0, rect.top - SOCIAL_ADD_MENU_GAP_PX - SOCIAL_ADD_MENU_EDGE_GAP_PX);
    const placement =
      spaceBelow >= 120 || spaceBelow >= spaceAbove ? "below" : "above";
    const top =
      placement === "below"
        ? rect.bottom + SOCIAL_ADD_MENU_GAP_PX
        : rect.top - SOCIAL_ADD_MENU_GAP_PX;

    let positionMode: "fixed" | "absolute" = "fixed";
    let resolvedLeft = left;
    let resolvedTop = top;

    if (portalId !== EMAIL_BUILDER_SETTINGS_DEFAULT_OVERLAY_PORTAL_ID) {
      const portalMount = document.getElementById(portalId);
      if (portalMount) {
        const mountRect = portalMount.getBoundingClientRect();
        resolvedLeft -= mountRect.left;
        resolvedTop -= mountRect.top;
        positionMode = "absolute";
      }
    }

    setPosition({
      left: resolvedLeft,
      top: resolvedTop,
      maxWidth,
      positionMode,
      placement,
    });
  }, [portalId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [close, open]);

  return (
    <>
      <span ref={triggerRef} className={"email-builder-settings-social-add-link-trigger"}>
        <Button
          type="primary"
          size="small"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => {
            if (open) {
              close();
              return;
            }
            setOpen(true);
          }}
        >
          {t("Add new link")}
        </Button>
      </span>
      {open && position ? (
        <Portal isMounted parentId={portalId}>
          <div
            ref={popoverRef}
            className={"email-builder-settings-social-add-menu"}
            role="menu"
            data-email-builder-social-add-menu=""
            style={{
              position: position.positionMode,
              left: position.left,
              top: position.top,
              width: position.maxWidth,
              transform: position.placement === "above" ? "translateY(-100%)" : undefined,
            }}
            onMouseDown={event => event.preventDefault()}
          >
            {availablePresets.map(network => (
              <button
                key={network}
                type="button"
                role="menuitem"
                className={"email-builder-settings-social-add-menu-item"}
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  onAddPreset(network);
                  close();
                }}
              >
                <span className={"email-builder-settings-social-add-menu-item-icon"}>
                  <img src={resolveSocialIconUrl(network, "white")} alt="" width={16} height={16} />
                </span>
                {t(SOCIAL_LABELS[network])}
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              className={"email-builder-settings-social-add-menu-item"}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                close();
                onRequestAddCustom();
              }}
            >
              <span className={"email-builder-settings-social-add-menu-item-icon"}>
                <span
                  className={"email-builder-settings-social-link-icon-placeholder"}
                  aria-hidden="true"
                />
              </span>
              {t("Custom link")}
            </button>
            {!canAdd ? (
              <p className={"email-builder-settings-social-add-menu-hint"}>
                {t("All preset icons are already added.")}
              </p>
            ) : null}
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export function SocialBlockSettings({
  block,
  patch,
}: BlockSettingsProps & { block: SocialBlock }) {
  const { t } = useTranslation();
  const adminFacility = useAppSelector(store => store.facilityStore.facility);
  const facility = useMemo(
    () => emailBuilderFacilityProfile(adminFacility),
    [adminFacility],
  );
  const [iconPickerTarget, setIconPickerTarget] = useState<SocialIconPickerTarget | null>(null);
  const availablePresets = availableSocialPresetNetworks(block.links);
  const canRemoveLink = block.links.length > MIN_SOCIAL_BLOCK_LINKS;
  const defaultPadding = baselineSocialPadding();
  const replaceIconPickerLink =
    iconPickerTarget?.kind === "replace"
      ? block.links.find(link => link.id === iconPickerTarget.linkId) ?? null
      : null;

  return (
    <>
      <SettingSection sectionId="content" title={t("Content")}>
        <div className={"email-builder-settings-social-links"}>
          <div className={"email-builder-settings-social-links-list"}>
            {block.links.map((link, index) => (
              <SocialLinkCard
                key={link.id}
                link={link}
                iconPreviewUrl={resolveSocialLinkIconUrl(link, block.iconVariant, facility)}
                canRemove={canRemoveLink}
                canMoveUp={index > 0}
                canMoveDown={index < block.links.length - 1}
                onPatch={linkPatch =>
                  patch({
                    links: patchSocialBlockLinkById(block.links, link.id, linkPatch),
                  })
                }
                onRemove={() =>
                  patch({
                    links: removeSocialBlockLink(block.links, link.id),
                  })
                }
                onMoveUp={() =>
                  patch({
                    links: moveSocialBlockLink(block.links, link.id, -1),
                  })
                }
                onMoveDown={() =>
                  patch({
                    links: moveSocialBlockLink(block.links, link.id, 1),
                  })
                }
                onChangeIcon={() => setIconPickerTarget({ kind: "replace", linkId: link.id })}
              />
            ))}
          </div>
          <div className={"email-builder-settings-social-links-footer"}>
            <SocialAddLinkPopover
              availablePresets={availablePresets}
              onAddPreset={network =>
                patch({
                  links: addPresetSocialBlockLink(block.links, network),
                })
              }
              onRequestAddCustom={() => setIconPickerTarget({ kind: "add-custom" })}
            />
          </div>
        </div>
      </SettingSection>

      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderAlignmentControl
          value={block.align}
          onChange={align => patch({ align })}
        />
        <EmailBuilderBoundedRange
          label={t("Icon size")}
          value={block.iconSize}
          min={16}
          max={48}
          defaultValue={baselineSocialIconSize()}
          onChange={iconSize => {
            if (iconSize === undefined) {
              return;
            }
            patch({ iconSize });
          }}
        />
        <EmailBuilderBoundedRange
          label={t("Gap")}
          value={block.gap}
          min={0}
          max={32}
          defaultValue={baselineSocialGap()}
          onChange={gap => {
            if (gap === undefined) {
              return;
            }
            patch({ gap });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...defaultPadding } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Social padding (px)")}
          value={block.padding}
          defaultValue={defaultPadding}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      {iconPickerTarget ? (
        <EmailBuilderMediaGalleryModal
          mode={iconPickerTarget.kind === "add-custom" ? "add" : "replace"}
          variant="socialIcon"
          isOpen
          initialMediaId={
            replaceIconPickerLink
              ? resolveSocialLinkIconMediaId(replaceIconPickerLink)
              : null
          }
          onClose={() => setIconPickerTarget(null)}
          onConfirm={({ media }) => {
            const icon = emailBuilderMediaItemToMediaRef(media);
            if (iconPickerTarget.kind === "add-custom") {
              patch({
                links: addCustomSocialBlockLink(block.links, icon),
              });
            } else {
              patch({
                links: patchSocialBlockLinkById(block.links, iconPickerTarget.linkId, {
                  icon,
                }),
              });
            }
            setIconPickerTarget(null);
          }}
        />
      ) : null}
    </>
  );
}
